import React from "react";
import { Table, Tag, Space, Button, Tooltip, Popconfirm } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DownloadOutlined,
  ReloadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const InspectionTable = ({
  inspections,
  loading,
  onViewDetails,
  onEdit,
  onDownloadPdf,
  onRegeneratePdf,
  onDelete,
  downloadingPdf,
  regeneratingPdf,
}) => {
  const columns = [
    {
      title: "Class",
      dataIndex: "className",
      key: "className",
      sorter: (a, b) => (a.className || "").localeCompare(b.className || ""),
    },
    {
      title: "Stream",
      dataIndex: "streamName",
      key: "streamName",
      render: (text) => text || "-",
    },
    {
      title: "Term",
      dataIndex: "termName",
      key: "termName",
      sorter: (a, b) => (a.termName || "").localeCompare(b.termName || ""),
    },
    {
      title: "Academic Year",
      dataIndex: "academicYear",
      key: "academicYear",
      sorter: (a, b) =>
        (a.academicYear || "").localeCompare(b.academicYear || ""),
    },
    {
      title: "General Score",
      dataIndex: "totalGeneralScore",
      key: "totalGeneralScore",
      align: "center",
      render: (score) => (
        <Tag color={score >= 70 ? "green" : score >= 50 ? "orange" : "red"}>
          {score}/100
        </Tag>
      ),
      sorter: (a, b) => (a.totalGeneralScore || 0) - (b.totalGeneralScore || 0),
    },
    {
      title: "Skill Score",
      dataIndex: "totalSkillScore",
      key: "totalSkillScore",
      align: "center",
      render: (score) => (
        <Tag color={score >= 210 ? "green" : score >= 150 ? "orange" : "red"}>
          {score}/300
        </Tag>
      ),
      sorter: (a, b) => (a.totalSkillScore || 0) - (b.totalSkillScore || 0),
    },
    {
      title: "Subjects",
      key: "subjects",
      align: "center",
      render: (_, record) => (
        <Tag color="blue">{record.subjectPerformances?.length || 0}</Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Download PDF">
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => onDownloadPdf(record)}
              loading={downloadingPdf === record.id}
              disabled={downloadingPdf === record.id}
            />
          </Tooltip>
          <Tooltip title="Regenerate PDF">
            <Button
              type="link"
              icon={<ReloadOutlined />}
              onClick={() => onRegeneratePdf(record)}
              loading={regeneratingPdf === record.id}
              disabled={regeneratingPdf === record.id}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Inspection"
            description="Are you sure you want to delete this inspection?"
            onConfirm={() => onDelete(record)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Tooltip title="Delete">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={inspections}
      rowKey="id"
      loading={loading}
      scroll={{ x: 1200 }}
      pagination={{
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} inspections`,
        defaultPageSize: 10,
        pageSizeOptions: ["10", "20", "50", "100"],
      }}
    />
  );
};

export default InspectionTable;
